import last from 'lodash/last';
import {VNode} from 'snabbdom/vnode';

import {findSelectedItems} from 'common/components/items-menu/utils';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {
  FilterOperator,
  FilterValue,
  ListQuantifier,
  NumberOperator,
  StringOperator,
} from 'common/widgets/property-filter-menu/models/types';
import {Section, SectionItem} from 'common/components/items-menu/types';
import {BookmarkDateRange} from 'common/widgets/date-range-picker/models/types';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {getSimilarDateRange} from 'common/widgets/date-range-picker-new/util';
import {DateRangeType} from 'common/widgets/date-range-picker/models/types';
import {getCurrentMoment} from 'common/util/date';
import {FiltersOperator, QueryFilterEntry, SelectorTypeDefinitions} from 'common/widgets/query-entry-section/types';
import {PropertyType} from 'common/report/constants';

import {PropertyTypeOperatorMap, SelectedOperator} from './constants';
import {FilterNumericValue} from './types/number';
import {FilterStringValue} from './types/string';
import {FilterListValue} from './types/list';
import {FilterObjectValue} from './types/object';
import {FilterDateValue} from './types/date';
import {FilterValueProps} from './types';

export const FilterValueTemplate: Record<PropertyType, (props: FilterValueProps) => VNode> = {
  [PropertyType.Number]: FilterNumericValue,
  [PropertyType.String]: FilterStringValue,
  [PropertyType.Object]: FilterObjectValue,
  [PropertyType.List]: FilterListValue,
  [PropertyType.Datetime]: FilterDateValue,
  // the following property type filters do not render a value definition
  [PropertyType.Boolean]: null,
  [PropertyType.Dimension]: null,
  [PropertyType.NonProperty]: null,
  [PropertyType.Unknown]: null,
};

export function getListQuantifierTooltip(listQuantifier: ListQuantifier) {
  return {
    text: `match ${listQuantifier}`,
  };
}

export function getFilterEntriesFromFilters(filters: PropertyFilter[], filtersOperator: FiltersOperator) {
  return filters.map((filter) => ({
    type: SelectorTypeDefinitions.Filter,
    event: filter as Partial<PropertyFilter>,
    filtersOperator: filtersOperator ?? FiltersOperator.And,
  })) as QueryFilterEntry[];
}

/**
 * Updates the selected filter operator and attemps to preserve
 * any pre-existing selected filter values
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
export function applyFilterOperatorToFilter(
  filterOperator: FilterOperator,
  currentFilter: PropertyFilter,
): PropertyFilter {
  const filter = currentFilter.setAttribute(`filterOperator`, filterOperator);

  const {filterValue: currentFilterValue, propertyType, propertyObjectKey} = currentFilter;
  const {filterValue: defaultFilterValue, filterOperator: defaultFilterOperator} = filter;

  if (propertyObjectKey?.length) {
    /** @todo: [shawn] Refactor into setAttribute */
    filter.propertyObjectKey = propertyObjectKey;
  }

  // when changing between filter operators, we want to transition to a valid filter state
  // in some cases, when there's not enough information to fully construct a new filter
  // for the selected operator, we omit the validation stage to allow for user input
  let performValueValidation = true;

  // attempts to preserve the currently selected date filter by applying the new selected
  // operator which maps to a DateRangeType
  if (propertyType === PropertyType.Datetime) {
    const oldDateRange = currentFilter.filterValue as DateRange;
    const similarDate = getSimilarDateRange(
      oldDateRange,
      getCurrentMoment({localized: true}),
      filterOperator as DateRangeType,
    );
    const filterValue = similarDate ? currentFilter.createDateRange(similarDate) : (defaultFilterValue as DateRange);
    if (filterValue.isValid() && filterValue.type === filterOperator) {
      filter.filterValue = filterValue;
      filter.filterOperator = filterValue.type;
    }
  } else if (currentFilterValue && (propertyType === PropertyType.Number || propertyType === PropertyType.String)) {
    // attempts to preserve the first selected filter value entry from an array filterValue
    const isCurrentValueArray = Array.isArray(currentFilterValue);
    const operatorRequiresArrayValue = [
      NumberOperator.Between,
      NumberOperator.NotBetween,
      StringOperator.Equals,
      StringOperator.DoesNotEqual,
    ].includes(filterOperator as NumberOperator);

    if (operatorRequiresArrayValue) {
      const isNumericFilter = propertyType === PropertyType.Number;
      performValueValidation = false;
      filter.filterValue = isCurrentValueArray
        ? // keeps selected array as new value
          currentFilterValue
        : // populates a numeric filter [value, value]
          ((isNumericFilter ? [currentFilterValue, null] : [currentFilterValue]) as FilterValue);
    } else {
      // grabs first element from selected value when possible
      filter.filterValue = isCurrentValueArray ? currentFilterValue[0] : currentFilterValue;
    }
  } else {
    filter.filterValue = currentFilterValue;
  }

  // when value preservation logic fails to end in a valid state, we revert
  // to the generated defaults by the property filter class
  if (performValueValidation && filter.isValid() === false) {
    filter.filterOperator = defaultFilterOperator;
    filter.filterValue = defaultFilterValue;
  }

  return filter;
}

export function getListQuantifierSections({listQuantifier}: PropertyFilter) {
  const items = [
    {label: `Any`, value: ListQuantifier.Any, isSelected: listQuantifier === ListQuantifier.Any},
    {label: `All`, value: ListQuantifier.All, isSelected: listQuantifier === ListQuantifier.All},
  ];
  return [{items}];
}

/**
 * Returns SecionItems for all avaialble filter operators
 * @param filter filter instance to fetch operators for
 * @param operatorOverrides if defined, overrides the operators presented to the user for a given filter
 */
export function getFilterOperatorsSections(
  filter: PropertyFilter,
  operatorOverrides: FilterOperator[] = null,
): Array<Section<SelectedOperator>> {
  const {propertyType, filterOperator, filterValue} = filter;
  const isDateRange = propertyType === PropertyType.Datetime;
  const operators =
    operatorOverrides ??
    (isDateRange ? filter.createDateRange().getDateRangeTypeOptions() : filter.getFilterOperators());
  const operatorMap = PropertyTypeOperatorMap.get(propertyType);

  const items: Array<SectionItem<SelectedOperator>> = Object.entries(operatorMap)
    .filter(([operator]) => operators.includes(operator as FilterOperator))
    .map(([operator, description]) => {
      return {
        label: `${description.symbol}` + (description.label ? ` (${description.label})` : ``),
        isSelected:
          isDateRange && filterValue
            ? (filterValue as BookmarkDateRange).type === operator
            : filterOperator === operator,
        value: [operator as FilterOperator, description],
      };
    });

  return [{items}];
}

export function getSelectedOperator(filter: PropertyFilter) {
  return last(findSelectedItems<SelectedOperator>(getFilterOperatorsSections(filter)))?.value;
}

export function getFilterOperatorTooltip(filter: PropertyFilter) {
  const [, mapping] = getSelectedOperator(filter);
  return mapping?.label && {text: mapping.label};
}

export function getFilterOperatorSymbol(filter: PropertyFilter) {
  const [, mapping] = getSelectedOperator(filter);
  return mapping.symbol;
}
